import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Save, X, Package } from 'lucide-react';
import { CollectionsService, ProductsService } from '../firebase';
import { Collection } from '../types/collection';
import { Product } from '../types/product';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  font-size: 1.8rem;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return 'background: #1a1a1a; color: white;';
      case 'danger':
        return 'background: #dc3545; color: white;';
      default:
        return 'background: #f8f9fa; color: #333; border: 1px solid #e1e5e9;';
    }
  }}

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CollectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const CollectionCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const CollectionCover = styled.div<{ $bgImage: string }>`
  width: 100%;
  height: 200px;
  background: url(${props => props.$bgImage}) center/cover;
  border-radius: 8px;
  margin-bottom: 15px;
  border: 2px solid #e1e5e9;
`;

const CollectionTitle = styled.h3`
  color: #333;
  font-size: 1.3rem;
  margin-bottom: 10px;
  font-weight: 600;
`;

const CollectionDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 15px;
  line-height: 1.5;
`;

const CollectionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const ProductCount = styled.span`
  font-size: 0.9rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 99999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProductsList = styled.div`
  margin-top: 20px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 15px;
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const ProductName = styled.span`
  flex: 1;
  color: #333;
`;

const EmptyCollectionMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const PreviewContainer = styled.div`
  margin-top: 10px;
`;

const ImagePreviewBox = styled.div<{ $bgImage: string }>`
  width: 100%;
  height: 200px;
  background: url(${props => props.$bgImage}) center/cover;
  border-radius: 8px;
  border: 2px solid #e1e5e9;
`;

const LabelNormal = styled(Label)`
  margin: 0;
  font-weight: normal;
`;

const EmptyProductMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
`;

const Message = styled.div<{ $type?: 'success' | 'error' }>`
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'error' ? '#fee' : '#efe'};
  color: ${props => props.$type === 'error' ? '#c33' : '#3c3'};
  border: 1px solid ${props => props.$type === 'error' ? '#fcc' : '#cfc'};
`;

const CollectionManagement: React.FC = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    coverImage: '',
    isActive: true,
    order: 0,
    products: [] as string[]
  });

  useEffect(() => {
    loadCollections();
    loadProducts();
  }, []);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const result = await CollectionsService.getCollections();
      if (result.success && result.data) {
        setCollections(result.data);
      }
    } catch (error) {
      console.error('Error loading collections:', error);
      setMessage({ text: 'Koleksiyonlar yüklenirken hata oluştu', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const result = await ProductsService.getProducts();
      if (result.success && result.data) {
        setProducts(result.data.filter(p => p.category === 'gelinlik'));
      }
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleOpenModal = (collection?: Collection) => {
    if (collection) {
      setEditingCollection(collection);
      setFormData({
        title: collection.title,
        description: collection.description,
        coverImage: collection.coverImage,
        isActive: collection.isActive,
        order: collection.order || 0,
        products: collection.products || []
      });
    } else {
      setEditingCollection(null);
      setFormData({
        title: '',
        description: '',
        coverImage: '',
        isActive: true,
        order: collections.length,
        products: []
      });
    }
    setIsModalOpen(true);
    setMessage(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCollection(null);
    setFormData({
      title: '',
      description: '',
      coverImage: '',
      isActive: true,
      order: 0,
      products: []
    });
    setMessage(null);
  };

  const convertGoogleDriveUrl = (url: string): string => {
    // Google Drive paylaşım linkini doğru formata dönüştür
    // Birden fazla formatı destekliyoruz
    const driveRegex = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
    const match = url.match(driveRegex);
    
    if (match && match[1]) {
      const fileId = match[1];
      // Thumbnail format kullan (CORS engeli olmayan alternatif)
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920`;
    }
    
    // Eğer zaten thumbnail veya uc formatındaysa olduğu gibi döndür
    if (url.includes('thumbnail') || url.includes('uc?export=view')) {
      return url;
    }
    
    return url;
  };

  const handleSaveCollection = async () => {
    if (!formData.title.trim() || !formData.coverImage.trim()) {
      setMessage({ text: 'Lütfen başlık ve kapak resmi ekleyin', type: 'error' });
      return;
    }

    try {
      // Google Drive linkini dönüştür
      const convertedCoverImage = convertGoogleDriveUrl(formData.coverImage);
      
      if (editingCollection && editingCollection.id) {
        const result = await CollectionsService.updateCollection(editingCollection.id, {
          title: formData.title,
          description: formData.description,
          coverImage: convertedCoverImage,
          isActive: formData.isActive,
          order: formData.order,
          products: formData.products
        });
        if (result.success) {
          setMessage({ text: 'Koleksiyon başarıyla güncellendi!', type: 'success' });
          await loadCollections();
          setTimeout(() => handleCloseModal(), 1500);
        } else {
          setMessage({ text: 'Koleksiyon güncellenirken hata oluştu', type: 'error' });
        }
      } else {
        const result = await CollectionsService.addCollection({
          title: formData.title,
          description: formData.description,
          coverImage: convertedCoverImage,
          isActive: formData.isActive,
          order: formData.order,
          products: formData.products
        });
        if (result.success) {
          setMessage({ text: 'Koleksiyon başarıyla eklendi!', type: 'success' });
          await loadCollections();
          setTimeout(() => handleCloseModal(), 1500);
        } else {
          setMessage({ text: 'Koleksiyon eklenirken hata oluştu', type: 'error' });
        }
      }
    } catch (error) {
      console.error('Error saving collection:', error);
      setMessage({ text: 'Bir hata oluştu', type: 'error' });
    }
  };

  const handleDeleteCollection = async (collectionId: string) => {
    if (!window.confirm('Bu koleksiyonu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const result = await CollectionsService.deleteCollection(collectionId);
      if (result.success) {
        setMessage({ text: 'Koleksiyon başarıyla silindi!', type: 'success' });
        await loadCollections();
      } else {
        setMessage({ text: 'Koleksiyon silinirken hata oluştu', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      setMessage({ text: 'Bir hata oluştu', type: 'error' });
    }
  };

  const handleToggleProduct = (productId: string) => {
    const index = formData.products.indexOf(productId);
    if (index > -1) {
      setFormData({
        ...formData,
        products: formData.products.filter(id => id !== productId)
      });
    } else {
      setFormData({
        ...formData,
        products: [...formData.products, productId]
      });
    }
  };

  if (loading) {
    return <Container>Yükleniyor...</Container>;
  }

  return (
    <Container>
      {message && (
        <Message $type={message.type}>
          {message.text}
        </Message>
      )}

      <Header>
        <Title>
          <Package size={28} />
          Koleksiyon Yönetimi
        </Title>
        <Button $variant="primary" onClick={() => handleOpenModal()}>
          <Plus size={18} />
          Yeni Koleksiyon
        </Button>
      </Header>

      <CollectionsGrid>
        {collections.map((collection) => (
          <CollectionCard key={collection.id}>
            <CollectionCover $bgImage={collection.coverImage} />
            <CollectionTitle>{collection.title}</CollectionTitle>
            <CollectionDescription>{collection.description}</CollectionDescription>
            <CollectionInfo>
              <ProductCount>
                <Package size={16} />
                {collection.products?.length || 0} Gelinlik
              </ProductCount>
              <StatusBadge $active={collection.isActive}>
                {collection.isActive ? 'Aktif' : 'Pasif'}
              </StatusBadge>
            </CollectionInfo>
            <Actions>
              <Button onClick={() => handleOpenModal(collection)}>
                <Edit size={16} />
                Düzenle
              </Button>
              <Button $variant="danger" onClick={() => collection.id && handleDeleteCollection(collection.id)}>
                <Trash2 size={16} />
                Sil
              </Button>
            </Actions>
          </CollectionCard>
        ))}
      </CollectionsGrid>

      {collections.length === 0 && (
        <EmptyCollectionMessage>
          Henüz koleksiyon eklenmedi. Yeni koleksiyon eklemek için yukarıdaki butona tıklayın.
        </EmptyCollectionMessage>
      )}

      {/* Add/Edit Modal */}
      <Modal $isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingCollection ? 'Koleksiyon Düzenle' : 'Yeni Koleksiyon'}
            </ModalTitle>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>

          {message && (
            <Message $type={message.type}>
              {message.text}
            </Message>
          )}

          <FormGroup>
            <Label>Koleksiyon Başlığı *</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Örn: 2026 Imperial Koleksiyonu"
            />
          </FormGroup>

          <FormGroup>
            <Label>Açıklama</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Koleksiyon açıklaması..."
            />
          </FormGroup>

          <FormGroup>
            <Label>Kapak Resmi Linki *</Label>
            <Input
              type="text"
              value={formData.coverImage}
              onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
              placeholder="https://..."
            />
            {formData.coverImage && (
              <PreviewContainer>
                <ImagePreviewBox $bgImage={formData.coverImage} />
              </PreviewContainer>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Sıra Numarası</Label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            />
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                id="isActive"
                aria-label="Koleksiyon aktif durumu"
              />
              <LabelNormal htmlFor="isActive">
                Aktif
              </LabelNormal>
            </CheckboxGroup>
          </FormGroup>

          <FormGroup>
            <Label>Koleksiyona Eklenecek Gelinlikler</Label>
            <ProductsList>
              {products.length === 0 ? (
                <EmptyProductMessage>
                  Henüz gelinlik eklenmemiş
                </EmptyProductMessage>
              ) : (
                products.map((product) => (
                  <ProductItem key={product.id}>
                    <CheckboxGroup>
                      <input
                        type="checkbox"
                        checked={formData.products.includes(product.id || '')}
                        onChange={() => product.id && handleToggleProduct(product.id)}
                        id={`product-${product.id}`}
                        aria-label={`${product.name} gelinliğini koleksiyona ekle`}
                      />
                      <LabelNormal htmlFor={`product-${product.id}`}>
                        <ProductName>{product.name}</ProductName>
                      </LabelNormal>
                    </CheckboxGroup>
                  </ProductItem>
                ))
              )}
            </ProductsList>
          </FormGroup>

          <ModalActions>
            <Button onClick={handleCloseModal}>
              <X size={18} />
              İptal
            </Button>
            <Button $variant="primary" onClick={handleSaveCollection}>
              <Save size={18} />
              Kaydet
            </Button>
          </ModalActions>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CollectionManagement;

